
import { Investor } from "@/types/investor";
import { enrichSingleInvestor } from "./singleProcessing";
import { processingInvestors, failedInvestors, markStateFresh } from "./processingState";
import { getAPIKey } from "@/utils/ai/apiKeyUtils";

// In-memory tracking of batch state for the current session
let currentBatchState = {
  totalCount: 0,
  processedCount: 0,
  isProcessing: false,
  startTime: null as Date | null,
  lastUpdate: null as Date | null
};

// Track processed investors in localStorage to help with recovery
const saveProgress = (processed: number, total: number) => {
  const progress = {
    processed,
    total,
    timestamp: new Date().toISOString()
  };
  localStorage.setItem("batch_progress", JSON.stringify(progress));
  
  // Update the current batch state
  currentBatchState.processedCount = processed;
  currentBatchState.lastUpdate = new Date();
  
  // Mark state as fresh to prevent unnecessary reloads
  markStateFresh();
};

/**
 * Process a batch of investors using AI - Completely sequential, blocking approach
 */
export const processBatch = async (
  batchSize: number,
  investors: Investor[],
  processedIds: Set<string>,
  relevanceContext: string,
  apiKey: string,
  onInvestorProcessed?: (investor: Investor) => Promise<boolean>
): Promise<Investor[]> => {
  // Reset in-memory state
  currentBatchState = {
    totalCount: batchSize,
    processedCount: 0,
    isProcessing: true,
    startTime: new Date(),
    lastUpdate: new Date()
  };
  
  // Clear previous progress marker if exists
  localStorage.removeItem("batch_completion");

  try {
    console.log(`Starting batch processing of ${batchSize} investors`);
    const openAiKey = apiKey || getAPIKey("openai");
    
    if (!openAiKey) {
      throw new Error("OpenAI API key not found");
    }
    
    // Ensure processedIds is a Set
    if (!(processedIds instanceof Set)) {
      console.warn("processedIds is not a Set, creating a new Set");
      processedIds = new Set(Array.isArray(processedIds) ? processedIds : []);
    }
    
    // Find investors that haven't been processed yet
    const unprocessedInvestors = investors.filter(inv => !processedIds.has(inv.id));
    console.log(`Found ${unprocessedInvestors.length} unprocessed investors`);
    
    if (unprocessedInvestors.length === 0) {
      console.log("No unprocessed investors found, returning original array");
      return investors;
    }
    
    // Process up to batchSize investors
    const investorsToProcess = unprocessedInvestors.slice(0, batchSize);
    console.log(`Processing ${investorsToProcess.length} investors in this batch`);
    saveProgress(0, investorsToProcess.length);
    
    // Create a new copy of the investors array to update
    const updatedInvestors = [...investors];
    
    // COMPLETELY SEQUENTIAL, ONE-BY-ONE PROCESSING - Process each investor one at a time
    for (let i = 0; i < investorsToProcess.length; i++) {
      const investor = investorsToProcess[i];
      
      // Skip if this investor is already being processed
      if (processingInvestors.has(investor.id)) {
        console.log(`Skipping investor ${investor.id} as it's already being processed`);
        continue;
      }
      
      try {
        // Mark investor as being processed
        processingInvestors.add(investor.id);
        
        // Process the investor - FULLY BLOCKING CALL
        console.log(`Processing investor ${i+1}/${investorsToProcess.length}: ${investor.firm || investor.id}`);
        const enrichedInvestor = await enrichSingleInvestor(
          investor,
          relevanceContext,
          openAiKey,
          null // No internal callback within enrichSingleInvestor
        );
        
        // Find index of this investor in our working array
        const index = updatedInvestors.findIndex(inv => inv.id === enrichedInvestor.id);
        
        if (index !== -1) {
          // Update the investor in our working array
          updatedInvestors[index] = enrichedInvestor;
          
          // Mark as processed
          processedIds.add(enrichedInvestor.id);
          
          // Update progress
          currentBatchState.processedCount = i + 1;
          saveProgress(i + 1, investorsToProcess.length);
          
          // Call the onInvestorProcessed callback if provided - WAIT FOR COMPLETION
          if (onInvestorProcessed) {
            console.log(`Running callback for investor ${enrichedInvestor.id}`);
            const callbackSuccess = await onInvestorProcessed(enrichedInvestor);
            console.log(`Callback for investor ${enrichedInvestor.id} ${callbackSuccess ? 'succeeded' : 'failed'}`);
          }
        } else {
          console.error(`Could not find investor ${enrichedInvestor.id} in the investors array`);
        }
      } catch (error) {
        console.error(`Error processing investor ${investor.id}:`, error);
        failedInvestors.set(investor.id, error as Error);
      } finally {
        // Remove from processing set regardless of success/failure
        processingInvestors.delete(investor.id);
      }
    }
    
    // Mark batch as complete in localStorage
    localStorage.setItem("batch_completion", JSON.stringify({
      timestamp: new Date().toISOString(),
      processed: currentBatchState.processedCount,
      // Add a stable identifier to prevent silent overwrites
      batchId: `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`
    }));
    
    // Reset the last activity timestamp to prevent stale state detection
    markStateFresh();
    
    return updatedInvestors;
  } catch (error) {
    console.error("Error in batch processing:", error);
    throw error;
  } finally {
    // Mark batch as complete
    currentBatchState.isProcessing = false;
    
    // Log completion
    console.log(`Batch processing complete. Processed ${currentBatchState.processedCount} investors.`);
  }
};

/**
 * Get current batch processing state
 */
export const getBatchProcessingState = () => {
  return {
    activeProcessing: currentBatchState.isProcessing,
    total: currentBatchState.totalCount,
    processed: currentBatchState.processedCount,
    startTime: currentBatchState.startTime,
    lastUpdate: currentBatchState.lastUpdate
  };
};
